import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { CountingEfficiency } from 'src/app/model/countingEfficiency';
import { ParticleSize } from 'src/app/model/particleSize';
import { ParticleTest, ParticleTestResult, ParticleTestType } from 'src/app/model/particleTest';
import { CountingEfficiencyService } from 'src/app/services/countigEfficiency.service';
import { ParticleSizeService } from 'src/app/services/particleSize.service';
import { SnackBarService } from 'src/app/services/snackBar.service';
import { ParticleTestEnum } from 'src/app/utils/particleTestUtils';
import { CalibrationStatus } from '../../../../model/particleTest';
import { ParticleCalibration } from '../../../../model/particleCalibration';
import { NumberUtils } from '../../../../utils/numberUtils';

export interface ParticlesEditDialogTestEdit {
  calibration: ParticleCalibration,
  test: ParticleTest;
  name: string;
  testTypes: ParticleTestType[];
}

@Component({
  templateUrl: './particles-edit-dialog-test-edit.component.html',
  standalone: false,
})
export class ParticlesEditDialogTestEditComponent {

  private readonly destroy$ = new Subject<void>();
  private pass;
  private fail;

  particleSizes: ParticleSize[];
  countingEfficiencies: CountingEfficiency[];

  constructor(
    private translate: TranslateService,
    public snackBarService: SnackBarService,
    private readonly particleSizeService: ParticleSizeService,
    private readonly countingEfficiencySercice: CountingEfficiencyService,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<ParticlesEditDialogTestEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ParticlesEditDialogTestEdit) {


  }

  ngOnInit(): void {
    this.particleSizeService.findAll().pipe(takeUntil(this.destroy$)).subscribe((sizes: ParticleSize[]) => {
      this.particleSizes = sizes;
    });
    this.countingEfficiencySercice.findAll().pipe(takeUntil(this.destroy$)).subscribe((count: CountingEfficiency[]) => {
      this.countingEfficiencies = count;
    });
  }

  onOkClick(): void {
    const errs: string[] = this.getErrors();
    if (errs.length === 0) {
      this.dialogRef.close(this.data.test);
    } else {
      const error = errs.join('\n');
      this.snackBarService.sendError(error);
    }
  }


  getErrors(): string[] {
    let errors = [];
    const essayName = this.getTestName(this.data.test.idTestType);

    if (this.data.test.idTestType === ParticleTestEnum.FLOW_RATE_AF) {
      if (!this.data.test.results) {
        errors.push(this.translate.instant('particlesTest.test.results.error', { essayName: essayName }))
      } else {
        this.data.test.results.forEach(vr => {
          if (!vr.nominal) {
            errors.push(this.translate.instant('particlesTest.test.nominal.error', { essayName: essayName }))
          }

          if (!vr.measured) {
            errors.push(this.translate.instant('particlesTest.test.measured.error', { essayName: essayName }))
          }

          if (!vr.limit) {
            errors.push(this.translate.instant('particlesTest.test.limit.error', { essayName: essayName }))
          }

          if (vr.result === null) {
            errors.push(this.translate.instant('particlesTest.test.result.error', { essayName: essayName }))
          }

          if (!vr.pass && !vr.fail) {
            errors.push(this.translate.instant('particlesTest.dialog.test.passfail.error', { essayName: essayName }))
          }
        });
      }
    }

    if (this.data.test.idTestType === ParticleTestEnum.FLOW_RATE_AL) {
      if (!this.data.test.results) {
        errors.push(this.translate.instant('particlesTest.test.results.error', { essayName: essayName }))
      } else {
        this.data.test.results.forEach(vr => {
          if (!vr.nominal) {
            errors.push(this.translate.instant('particlesTest.test.nominal.error', { essayName: essayName }))
          }

          if (!vr.measured) {
            errors.push(this.translate.instant('particlesTest.test.measured.error', { essayName: essayName }))
          }

          if (!vr.limit) {
            errors.push(this.translate.instant('particlesTest.test.limit.error', { essayName: essayName }))
          }

          if (vr.result === null) {
            errors.push(this.translate.instant('particlesTest.test.result.error', { essayName: essayName }))
          }

          if (!vr.pass && !vr.fail) {
            errors.push(this.translate.instant('particlesTest.dialog.test.passfail.error', { essayName: essayName }))
          }
        });
      }
    }

    if (this.data.test.idTestType === ParticleTestEnum.SIZE_SETTING_AF) {
      if (!this.data.test.results) {
        errors.push(this.translate.instant('particlesTest.test.results.error', { essayName: essayName }))
      } else {
        this.data.test.results.forEach(vr => {
          if (!vr.channelPha) {
            errors.push(this.translate.instant('particlesTest.test.channel.error', { essayName: essayName }))
          }

          if (!vr.previousValue) {
            errors.push(this.translate.instant('particlesTest.test.previousValue.error', { essayName: essayName }))
          }

          if (!vr.median) {
            errors.push(this.translate.instant('particlesTest.test.median.error', { essayName: essayName }))
          }

          if (!vr.ajustedValue) {
            errors.push(this.translate.instant('particlesTest.test.ajustedValue.error', { essayName: essayName }))
          }

          if (!vr.limit) {
            errors.push(this.translate.instant('particlesTest.test.limit.error', { essayName: essayName }))
          }

          if (vr.result === null) {
            errors.push(this.translate.instant('particlesTest.test.result.error', { essayName: essayName }))
          }

          if (!vr.pass && !vr.fail) {
            errors.push(this.translate.instant('particlesTest.dialog.test.passfail.error', { essayName: essayName }))
          }
        });
      }
    }

    if (this.data.test.idTestType === ParticleTestEnum.SIZE_SETTING_AL) {
      if (!this.data.test.results) {
        errors.push(this.translate.instant('particlesTest.test.results.error', { essayName: essayName }))
      } else {
        this.data.test.results.forEach(vr => {
          if (!vr.channelPha) {
            errors.push(this.translate.instant('particlesTest.test.channel.error', { essayName: essayName }))
          }

          if (!vr.previousValue) {
            errors.push(this.translate.instant('particlesTest.test.previousValue.error', { essayName: essayName }))
          }

          if (!vr.median) {
            errors.push(this.translate.instant('particlesTest.test.median.error', { essayName: essayName }))
          }

          if (!vr.ajustedValue) {
            errors.push(this.translate.instant('particlesTest.test.ajustedValue.error', { essayName: essayName }))
          }

          if (!vr.limit) {
            errors.push(this.translate.instant('particlesTest.test.limit.error', { essayName: essayName }))
          }

          if (vr.result === null) {
            errors.push(this.translate.instant('particlesTest.test.result.error', { essayName: essayName }))
          }

          if (!vr.pass && !vr.fail) {
            errors.push(this.translate.instant('particlesTest.dialog.test.passfail.error', { essayName: essayName }))
          }
        })
      }
    }

    if (this.data.test.idTestType === ParticleTestEnum.COUNTING_EFFICIENCY_AF) {
      if (!this.data.test.results) {
        errors.push(this.translate.instant('particlesTest.test.results.error', { essayName: essayName }))
      } else {
        this.data.test.results.forEach(vr => {
          if (!vr.efficiencyTest) {
            errors.push(this.translate.instant('particlesTest.test.efficiencyTest.error', { essayName: essayName }))
          }

          if (!vr.obtainedEfficiency) {
            errors.push(this.translate.instant('particlesTest.test.obtainedEfficiency.error', { essayName: essayName }))
          }

          if (!vr.size) {
            errors.push(this.translate.instant('particlesTest.test.size.error', { essayName: essayName }))
          }

          if (!vr.limit) {
            errors.push(this.translate.instant('particlesTest.test.limit.error', { essayName: essayName }))
          }

          if (!vr.pass && !vr.fail) {
            errors.push(this.translate.instant('particlesTest.dialog.test.passfail.error', { essayName: essayName }))
          }
        });
      }
    }

    if (this.data.test.idTestType === ParticleTestEnum.COUNTING_EFFICIENCY_AL) {
      if (!this.data.test.results) {
        errors.push(this.translate.instant('particlesTest.test.results.error', { essayName: essayName }))
      } else {
        this.data.test.results.forEach(vr => {
          if (!vr.efficiencyTest) {
            errors.push(this.translate.instant('particlesTest.test.efficiencyTest.error', { essayName: essayName }))
          }

          if (!vr.obtainedEfficiency) {
            errors.push(this.translate.instant('particlesTest.test.obtainedEfficiency.error', { essayName: essayName }))
          }

          if (!vr.size) {
            errors.push(this.translate.instant('particlesTest.test.size.error', { essayName: essayName }))
          }

          if (!vr.limit) {
            errors.push(this.translate.instant('particlesTest.test.limit.error', { essayName: essayName }))
          }

          if (!vr.pass && !vr.fail) {
            errors.push(this.translate.instant('particlesTest.dialog.test.passfail.error', { essayName: essayName }))
          }
        })
      }
    }

    if (this.data.test.idTestType === ParticleTestEnum.PEAK_NOISE_AF) {
      if (!this.data.test.results) {
        errors.push(this.translate.instant('particlesTest.test.results.error', { essayName: essayName }))
      } else {
        this.data.test.results.forEach(vr => {
          if (!vr.noise) {
            errors.push(this.translate.instant('particlesTest.test.measuredNoise.error', { essayName: essayName }))
          }

          if (!vr.signal) {
            errors.push(this.translate.instant('particlesTest.test.measuredSignal.error', { essayName: essayName }))
          }

          if (!vr.limit) {
            errors.push(this.translate.instant('particlesTest.test.limit.error', { essayName: essayName }))
          }

          if (vr.result === null) {
            errors.push(this.translate.instant('particlesTest.test.result.error', { essayName: essayName }))
          }

          if (!vr.pass && !vr.fail) {
            errors.push(this.translate.instant('particlesTest.dialog.test.passfail.error', { essayName: essayName }))
          }
        });
      }
    }

    if (this.data.test.idTestType === ParticleTestEnum.PEAK_NOISE_AL) {
      if (!this.data.test.results) {
        errors.push(this.translate.instant('particlesTest.test.results.error', { essayName: essayName }))
      } else {
        this.data.test.results.forEach(vr => {
          if (!vr.noise) {
            errors.push(this.translate.instant('particlesTest.test.measuredNoise.error', { essayName: essayName }))
          }

          if (!vr.signal) {
            errors.push(this.translate.instant('particlesTest.test.measuredSignal.error', { essayName: essayName }))
          }

          if (!vr.limit) {
            errors.push(this.translate.instant('particlesTest.test.limit.error', { essayName: essayName }))
          }

          if (vr.result === null) {
            errors.push(this.translate.instant('particlesTest.test.result.error', { essayName: essayName }))
          }

          if (!vr.pass && !vr.fail) {
            errors.push(this.translate.instant('particlesTest.dialog.test.passfail.error', { essayName: essayName }))
          }
        });
      }
    }

    if (this.data.test.idTestType === ParticleTestEnum.SLAMPING_TIME_AF) {
      if (!this.data.test.results) {
        errors.push(this.translate.instant('particlesTest.test.results.error', { essayName: essayName }))
      } else {
        this.data.test.results.forEach(vr => {
          if (!vr.countTime) {
            errors.push(this.translate.instant('particlesTest.test.count_time.error', { essayName: essayName }))
          }

          if (!vr.standarTime) {
            errors.push(this.translate.instant('particlesTest.test.standar_time.error', { essayName: essayName }))
          }

          if (!vr.limit) {
            errors.push(this.translate.instant('particlesTest.test.limit.error', { essayName: essayName }))
          }

          if (vr.result === null) {
            errors.push(this.translate.instant('particlesTest.test.result.error', { essayName: essayName }))
          }

          if (!vr.pass && !vr.fail) {
            errors.push(this.translate.instant('particlesTest.dialog.test.passfail.error', { essayName: essayName }))
          }
        });
      }
    }

    if (this.data.test.idTestType === ParticleTestEnum.SLAMPING_TIME_AL) {
      if (!this.data.test.results) {
        errors.push(this.translate.instant('particlesTest.test.results.error', { essayName: essayName }))
      } else {
        this.data.test.results.forEach(vr => {
          if (!vr.countTime) {
            errors.push(this.translate.instant('particlesTest.test.count_time.error', { essayName: essayName }))
          }

          if (!vr.standarTime) {
            errors.push(this.translate.instant('particlesTest.test.standar_time.error', { essayName: essayName }))
          }

          if (!vr.limit) {
            errors.push(this.translate.instant('particlesTest.test.limit.error', { essayName: essayName }))
          }

          if (vr.result === null) {
            errors.push(this.translate.instant('particlesTest.test.result.error', { essayName: essayName }))
          }

          if (!vr.pass && !vr.fail) {
            errors.push(this.translate.instant('particlesTest.dialog.test.passfail.error', { essayName: essayName }))
          }
        })
      }
    }

    if (this.data.test.idTestType === ParticleTestEnum.FALSE_COUNT_AF) {
      if (!this.data.test.results) {
        errors.push(this.translate.instant('particlesTest.test.results.error', { essayName: essayName }))
      } else {
        this.data.test.results.forEach(vr => {
          if (vr.obsAccount === null || vr.obsAccount === undefined) {
            errors.push(this.translate.instant('particlesTest.test.obs_account.error', { essayName: essayName }))
          }

          if (!vr.limit) {
            errors.push(this.translate.instant('particlesTest.test.limit.error', { essayName: essayName }))
          }

          if (!vr.pass && !vr.fail) {
            errors.push(this.translate.instant('particlesTest.dialog.test.passfail.error', { essayName: essayName }))
          }
        });
      }
    }

    if (this.data.test.idTestType === ParticleTestEnum.FALSE_COUNT_AL) {
      if (!this.data.test.results) {
        errors.push(this.translate.instant('particlesTest.test.results.error', { essayName: essayName }))
      } else {
        this.data.test.results.forEach(vr => {
          if (vr.obsAccount === null || vr.obsAccount === undefined ) {
            errors.push(this.translate.instant('particlesTest.test.obs_account.error', { essayName: essayName }))
          }

          if (!vr.limit) {
            errors.push(this.translate.instant('particlesTest.test.limit.error', { essayName: essayName }))
          }

          if (!vr.pass && !vr.fail) {
            errors.push(this.translate.instant('particlesTest.dialog.test.passfail.error', { essayName: essayName }))
          }
        })
      }
    }

    if (this.data.test.idTestType === ParticleTestEnum.SIZE_RESOLUTION_AF) {
      if (!this.data.test.results) {
        errors.push(this.translate.instant('particlesTest.test.results.error', { essayName: essayName }))
      } else {
        this.data.test.results.forEach(vr => {
          if (!vr.certSize) {
            errors.push(this.translate.instant('particlesTest.test.cert_size.error', { essayName: essayName }))
          }

          if (!vr.obtResolution) {
            errors.push(this.translate.instant('particlesTest.test.obt_resolution.error', { essayName: essayName }))
          }

          if (!vr.limit) {
            errors.push(this.translate.instant('particlesTest.test.limit.error', { essayName: essayName }))
          }

          if (!vr.pass && !vr.fail) {
            errors.push(this.translate.instant('particlesTest.dialog.test.passfail.error', { essayName: essayName }))
          }
        });
      }
    }

    if (this.data.test.idTestType === ParticleTestEnum.SIZE_RESOLUTION_AL) {
      if (!this.data.test.results) {
        errors.push(this.translate.instant('particlesTest.test.results.error', { essayName: essayName }))
      } else {
        this.data.test.results.forEach(vr => {
          if (!vr.certSize) {
            errors.push(this.translate.instant('particlesTest.test.cert_size.error', { essayName: essayName }))
          }

          if (!vr.obtResolution) {
            errors.push(this.translate.instant('particlesTest.test.obt_resolution.error', { essayName: essayName }))
          }

          if (!vr.limit) {
            errors.push(this.translate.instant('particlesTest.test.limit.error', { essayName: essayName }))
          }

          if (!vr.pass && !vr.fail) {
            errors.push(this.translate.instant('particlesTest.dialog.test.passfail.error', { essayName: essayName }))
          }
        })
      }
    }

    return errors;
  }

  onNoClick(): void {
    this.dialogRef.close(null);
  }

  showNominal(): boolean {
    const visibleIds = [ParticleTestEnum.FLOW_RATE_AF, ParticleTestEnum.FLOW_RATE_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showChannel(): boolean {
    const visibleIds = [ParticleTestEnum.SIZE_SETTING_AF, ParticleTestEnum.SIZE_SETTING_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  isLimitEditable(): boolean {
    const visibleIds = [
      ParticleTestEnum.FLOW_RATE_AF,
      ParticleTestEnum.SIZE_SETTING_AF,
      ParticleTestEnum.PEAK_NOISE_AF,
      ParticleTestEnum.SIZE_RESOLUTION_AF,
      ParticleTestEnum.FLOW_RATE_AL,
      ParticleTestEnum.SIZE_SETTING_AL,
      ParticleTestEnum.PEAK_NOISE_AL,
      ParticleTestEnum.SIZE_RESOLUTION_AL,
    ];

    return visibleIds.includes(this.data.test.idTestType);
  }

  showLimit(): boolean {
    const visibleIds = [
      ParticleTestEnum.FLOW_RATE_AF,
      ParticleTestEnum.SIZE_SETTING_AF,
      ParticleTestEnum.COUNTING_EFFICIENCY_AF,
      ParticleTestEnum.PEAK_NOISE_AF,
      ParticleTestEnum.SLAMPING_TIME_AF,
      ParticleTestEnum.FALSE_COUNT_AF,
      ParticleTestEnum.SIZE_RESOLUTION_AF,
      ParticleTestEnum.FLOW_RATE_AL,
      ParticleTestEnum.SIZE_SETTING_AL,
      ParticleTestEnum.COUNTING_EFFICIENCY_AL,
      ParticleTestEnum.PEAK_NOISE_AL,
      ParticleTestEnum.SLAMPING_TIME_AL,
      ParticleTestEnum.FALSE_COUNT_AL,
      ParticleTestEnum.SIZE_RESOLUTION_AL,
    ];

    return visibleIds.includes(this.data.test.idTestType);
  }

  showMessuared(): boolean {
    const visibleIds = [ParticleTestEnum.FLOW_RATE_AF, ParticleTestEnum.FLOW_RATE_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showResult(): boolean {
    const visibleIds = [
      ParticleTestEnum.FLOW_RATE_AF,
      ParticleTestEnum.SIZE_SETTING_AF,
      ParticleTestEnum.PEAK_NOISE_AF,
      ParticleTestEnum.SLAMPING_TIME_AF,
      ParticleTestEnum.FLOW_RATE_AL,
      ParticleTestEnum.SIZE_SETTING_AL,
      ParticleTestEnum.PEAK_NOISE_AL,
      ParticleTestEnum.SLAMPING_TIME_AL,
    ];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showMedian(): boolean {
    const visibleIds = [ParticleTestEnum.SIZE_SETTING_AF, ParticleTestEnum.SIZE_SETTING_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showPreviousValue(): boolean {
    const visibleIds = [ParticleTestEnum.SIZE_SETTING_AF, ParticleTestEnum.SIZE_SETTING_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showAjustedValue(): boolean {
    const visibleIds = [ParticleTestEnum.SIZE_SETTING_AF, ParticleTestEnum.SIZE_SETTING_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showSize(): boolean {
    const visibleIds = [ParticleTestEnum.COUNTING_EFFICIENCY_AF, ParticleTestEnum.COUNTING_EFFICIENCY_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showObtainedEfficiency(): boolean {
    const visibleIds = [ParticleTestEnum.COUNTING_EFFICIENCY_AF, ParticleTestEnum.COUNTING_EFFICIENCY_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showMeasuredNoise(): boolean {
    const visibleIds = [ParticleTestEnum.PEAK_NOISE_AF, ParticleTestEnum.PEAK_NOISE_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showMeasuredSignal(): boolean {
    const visibleIds = [ParticleTestEnum.PEAK_NOISE_AF, ParticleTestEnum.PEAK_NOISE_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showEfficiencyTest(): boolean {
    const visibleIds = [ParticleTestEnum.COUNTING_EFFICIENCY_AF, ParticleTestEnum.COUNTING_EFFICIENCY_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showObsAccounts(): boolean {
    const visibleIds = [ParticleTestEnum.FALSE_COUNT_AF, ParticleTestEnum.FALSE_COUNT_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showObtResolucion(): boolean {
    const visibleIds = [ParticleTestEnum.SIZE_RESOLUTION_AF, ParticleTestEnum.SIZE_RESOLUTION_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showSizeCertificate(): boolean {
    const visibleIds = [ParticleTestEnum.SIZE_RESOLUTION_AF, ParticleTestEnum.SIZE_RESOLUTION_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showCountTime(): boolean {
    const visibleIds = [ParticleTestEnum.SLAMPING_TIME_AF, ParticleTestEnum.SLAMPING_TIME_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  showStandarTime(): boolean {
    const visibleIds = [ParticleTestEnum.SLAMPING_TIME_AF, ParticleTestEnum.SLAMPING_TIME_AL];
    return visibleIds.includes(this.data.test.idTestType);
  }

  onPassChange(isPass: boolean, test: ParticleTestResult): void {
    test.pass = isPass;
    test.fail = !isPass;
  }

  onFailChange(isFail: boolean, test: ParticleTestResult): void {
    test.fail = isFail;
    test.pass = !isFail;
  }


  calculateResult(test: ParticleTestResult, idTestType: number) {

    if (idTestType == ParticleTestEnum.FLOW_RATE_AF || idTestType == ParticleTestEnum.FLOW_RATE_AL) { this.calculateFlowRate(test) }
    else if (idTestType == ParticleTestEnum.SIZE_SETTING_AF || idTestType == ParticleTestEnum.SIZE_SETTING_AL) { this.calculateSizeSetting(test) }
    else if (idTestType == ParticleTestEnum.PEAK_NOISE_AF || idTestType == ParticleTestEnum.PEAK_NOISE_AL) { this.calculatePeakNoise(test) }
    else if (idTestType == ParticleTestEnum.SLAMPING_TIME_AF || idTestType == ParticleTestEnum.SLAMPING_TIME_AL) { this.calculateSlamplingTime(test) }

  }

  calculateFlowRate(test: ParticleTestResult) {

    if (test.nominal && test.nominal !== 0 && test.measured) {
      const diferencia = test.measured - test.nominal;
      const porcentaje = (diferencia / test.nominal) * 100;
      test.result = NumberUtils.roundNumber(porcentaje, 2) + '%';
      const match = test.limit.match(/<\s*(\d+(\.\d+)?)/);
      const limit = match ? parseFloat(match[1]) : null;

      if (Math.abs(porcentaje) < Number(limit)) {
        test.pass = true;
        test.fail = false;
      } else {
        test.pass = false;
        test.fail = true;
      }
    } else {
      test.result = null;
    }
  }
  calculateSizeSetting(test: ParticleTestResult) {
    if (test.ajustedValue && test.previousValue !== 0) {
      const diferencia = test.ajustedValue - test.previousValue;
      const porcentaje = (diferencia / test.previousValue) * 100;
      test.result = NumberUtils.roundNumber(porcentaje, 2) + '%';
      const match = test.limit.match(/\d+(\.\d+)?/);
      const limit = match ? parseFloat(match[0]) : null;
      if (Math.abs(porcentaje) <= Number(limit)) {

        test.pass = true;
        test.fail = false;
      } else {
        test.pass = false;
        test.fail = true;
      }
    } else {
      test.result = null;
    }
  }

  falseCountPass(test: ParticleTestResult) {
    //Se coge el primer nunmero del limite del test <= 1cnt / 5 min por que tiene que ser menor o igual a uno
    const match = test.limit.match(/\d+(\.\d+)?/);
    const limit = match ? parseFloat(match[0]) : null;

    if (test.obsAccount <= limit) {
      test.pass = true;
      test.fail = false;
    } else {
      test.pass = false;
      test.fail = true;
    }
  }

  calculatePeakNoise(test: ParticleTestResult) {
    if (test.noise && test.signal !== 0) {
      const porcentaje = (test.signal / test.noise);
      test.result = `${NumberUtils.round(porcentaje, 2)}`;
      const match = test.limit.match(/\d+(\.\d+)?/);
      const limit = match ? parseFloat(match[0]) : null;
      if (Math.abs(porcentaje) >= Number(limit)) {

        test.pass = true;
        test.fail = false;
      } else {
        test.pass = false;
        test.fail = true;
      }
    } else {
      test.result = null;
    }
  }

  calculateSlamplingTime(test: ParticleTestResult): void {
    if (test.countTime && test.standarTime && test.standarTime !== 0) {
      const error = ((test.countTime - test.standarTime) / test.standarTime) * 100;
      test.result = `${NumberUtils.roundNumber(error, 2)}`;
      //del limite 60 ± 1% tiene que coger el 1 que es el margen de error que hay 
      const match = test.limit.match(/±\s*(\d+(\.\d+)?)/);
      const limit = match ? parseFloat(match[1]) : null;

      if (Math.abs(error) <= limit) {
        test.pass = true;
        test.fail = false;
      } else {
        test.pass = false;
        test.fail = true;
      }
    } else {
      test.result = null;
      test.pass = false;
      test.fail = false;
    }
  }

  addTestResult(test: ParticleTest) {
    if (test.idTestType == ParticleTestEnum.COUNTING_EFFICIENCY_AF || test.idTestType == ParticleTestEnum.COUNTING_EFFICIENCY_AL) {
      const newResult = new ParticleTestResult();
      this.countingEfficiencies.forEach(count => {
        if (count.efficiency === "100%") {
          newResult.efficiencyTest = count.efficiency;
          newResult.limit = count.lowerLimit + "-" + count.upperLimit;
        }
      });
      test.results.push(newResult);
    } else if (test.idTestType == ParticleTestEnum.SLAMPING_TIME_AF) {
      const result = new ParticleTestResult();
      result.limit = "60 ± 1%";
      test.results.push(result);
    }  else if (test.idTestType == ParticleTestEnum.SLAMPING_TIME_AL) {
      const result = new ParticleTestResult();
      result.limit = "60 ± 1%";
      test.results.push(result);
    } else if (test.idTestType == ParticleTestEnum.FALSE_COUNT_AF) {
      const result = new ParticleTestResult();
      result.limit = "<= 1cnt / 5 min";
      test.results.push(result);
     } else if (test.idTestType == ParticleTestEnum.FALSE_COUNT_AL) {
      const result = new ParticleTestResult();
      result.limit = "<= 1cnt / 5 min";
      test.results.push(result);
     } else {
      const newResult = new ParticleTestResult();
      newResult.limit = "<" + this.data.testTypes.find(item => item.id === test.idTestType).limite.toString() + "%";
      test.results.push(newResult);
    }
  }

  removeTestResult(index: number): void {
    this.data.test.results.splice(index, 1);
  }

  isEditable(): boolean {
    return this.data.calibration.idStatus === CalibrationStatus.FIRMADO || this.data.calibration.idStatus === CalibrationStatus.NO_VALIDO;
  }

  getTestName(id: number): string {
    return this.translate.instant("particleTestType." + this.data.testTypes.find(tt => tt.id === id).translation);
  }

}